import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import { evaluateHomePlanCreationEligibility } from "api-types/locations/homePlanCreationEligibility";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import type { CommunityPlan } from "api-types/modelTypes";
import Flags from "common/constants/flags";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { ARCHIVE_MULTI_UNIT_PLAN_MESSAGE } from "common/utils/multiUnitPlans";
import { getHomePlanCreationBlockMessage } from "lib/components/builders/communities/plans/homePlanCreationRules";
import { isMasterPlanMultiUnit } from "lib/components/builders/communities/plans/planUtils";
import type { PublicPlanType } from "lib/requestTypes";
import { configurePublicPlanPath, useAppRouterParams } from "lib/routeHelpers";
import { userCanUseStudio } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import { copyTextWithNotification } from "lib/utils/clipboard";
import { canDownloadBuiltInPlanSets } from "lib/utils/planDocumentPermissions";
import type { ReactNode } from "react";
import Link from "ui/components/Link";
import useAppContext from "ui/hooks/useAppContext";
import IconCopy from "ui/icons/IconCopy";
import IconDocLandscape from "ui/icons/IconDocLandscape";
import IconDocSpreadsheet from "ui/icons/IconDocSpreadsheet";
import IconEditSquare from "ui/icons/IconEditSquare";
import IconFileOpen from "ui/icons/IconFileOpen";
import IconFolder from "ui/icons/IconFolder";
import IconShowroom from "ui/icons/IconShowroom";
import IconTrash from "ui/icons/IconTrash";

interface PlanOverflowMenuProps {
  onUpdatePlan?: (() => void) | null;
  onDeletePlan?: (() => void) | null;
  onArchivePlan?: (() => void) | null;
  onUnarchivePlan?: (() => void) | null;
  onDownloadPlan?: (() => void) | null;
  studioPath?: string;
  masterPlanId?: string;
  showroomPath?: string | null;
  showroomIntroPath?: string;
  editShowroomIntroPath?: string;
  procurePath?: string;
  communityPlanId?: string;
  publicPlanType?: PublicPlanType | null;
  communityPlan?: CommunityPlan | ClientLocationPlan;
  masterPlan?: ClientMasterPlan;
  locationSalesAgentId?: string | null;
  /** Location with level — used to gate Copy Public Link for location plans */
  location?: ClientLocationWithLevel;
}

export default function usePlanOverflowMenuItems({
  onUpdatePlan,
  onDeletePlan,
  onArchivePlan,
  onUnarchivePlan,
  onDownloadPlan,
  studioPath,
  masterPlanId,
  showroomPath,
  showroomIntroPath,
  editShowroomIntroPath,
  procurePath,
  publicPlanType,
  communityPlanId,
  masterPlan,
  communityPlan,
  locationSalesAgentId,
  location,
}: PlanOverflowMenuProps): ReactNode[] {
  const planId = publicPlanType === "master_plans" ? masterPlanId : communityPlanId;
  const planIsMultiUnit = isMasterPlanMultiUnit(masterPlan);
  const { setUserNotification } = useAppContext();
  const { builderId } = useAppRouterParams();
  const userContext = useUserContext();
  const { enabledFeatures, enabledProducts, hasPermission, user } = userContext;

  const publicLinkBlockMessage =
    publicPlanType === "community_plans" && location
      ? getHomePlanCreationBlockMessage(
          evaluateHomePlanCreationEligibility({
            atMultiUnitLocation: isMultiUnitLocation(location),
            planIsMultiUnit,
          }),
        )
      : undefined;

  const hasPermissionForResource = (action: "EDIT_COMMUNITY_PLANS", resourceId?: string): boolean => {
    if (!resourceId) {
      return false;
    }

    return hasPermission(action, { resourceId });
  };

  const canEditCommunityPlan =
    publicPlanType === "community_plans" ? hasPermissionForResource("EDIT_COMMUNITY_PLANS", communityPlanId) : false;

  const canEditPlanDetails =
    publicPlanType === "community_plans" ? canEditCommunityPlan : hasPermission("EDIT_HOME_PLANS");

  const handlePublicLinkClick = async (): Promise<void> => {
    assertIsNotNullOrUndefined(publicPlanType);
    assertIsNotNullOrUndefined(builderId);
    assertIsNotNullOrUndefined(planId);

    const link = Flags.absoluteUrl(configurePublicPlanPath(publicPlanType, builderId, planId));
    const copyNotification = await copyTextWithNotification(link);

    setUserNotification(copyNotification);
  };

  const menuItems: ReactNode[] = [];

  const planDownloadEnabled = (() => {
    const canDownloadBuiltIn = canDownloadBuiltInPlanSets({
      hasPermission,
      userId: user?.id,
      locationSalesAgentId,
    });

    if (!canDownloadBuiltIn) {
      return false;
    }

    let downloadEnabled = false;

    if (masterPlan) {
      downloadEnabled = masterPlan.planDownloadEnabled;
    }

    if (communityPlan) {
      downloadEnabled = downloadEnabled && communityPlan.planDownloadEnabled;
    }

    return downloadEnabled;
  })();

  if (planDownloadEnabled) {
    menuItems.push(
      <Link key="download_plan" onClick={onDownloadPlan !== null ? onDownloadPlan : undefined}>
        <IconDocLandscape size={14} />
        Download {enabledFeatures.userDefinedPlansets ? "Documentation (PDF)" : "Plan Set"}
      </Link>,
    );
  }

  if (canEditPlanDetails && onUpdatePlan) {
    menuItems.push(
      <Link key="edit_details" onClick={onUpdatePlan}>
        <IconEditSquare size={14} />
        Edit Plan Details
      </Link>,
    );
  }

  if (publicPlanType) {
    menuItems.push(
      <Link
        key="public_link"
        onClick={publicLinkBlockMessage ? undefined : handlePublicLinkClick}
        disabled={!!publicLinkBlockMessage}
        tooltip={publicLinkBlockMessage}
      >
        <IconCopy size={14} />
        Copy Public Link
      </Link>,
    );
  }

  if (showroomPath) {
    menuItems.push(
      <Link key="showroom" href={showroomPath}>
        <IconShowroom size={14} />
        View in Showroom
      </Link>,
    );
  }
  if (hasPermission("EDIT_SHOWROOM_INTRO") && editShowroomIntroPath) {
    menuItems.push(
      <Link key="editShowroomIntro" href={editShowroomIntroPath}>
        <IconShowroom size={14} />
        Configure Showroom Intro
      </Link>,
    );
  }
  if (showroomIntroPath) {
    menuItems.push(
      <Link key="showroomIntro" href={showroomIntroPath}>
        <IconShowroom size={14} />
        View in Showroom Intro
      </Link>,
    );
  }

  if (procurePath && hasPermission("VIEW_PROCURE") && enabledProducts.procure) {
    menuItems.push(
      <Link key="procure_view" href={procurePath}>
        <IconDocSpreadsheet size={14} /> View in Estimate
      </Link>,
    );
  }

  if (studioPath && userCanUseStudio(userContext)) {
    menuItems.push(
      <Link key="open_studio_file" href={studioPath}>
        <IconFileOpen size={14} />
        Open Studio File
      </Link>,
    );
  }

  if (
    onArchivePlan &&
    ((publicPlanType === "master_plans" && hasPermission("EDIT_MASTER_PLANS")) || canEditCommunityPlan)
  ) {
    menuItems.push(
      <Link
        key="archive_plan"
        onClick={onArchivePlan}
        disabled={planIsMultiUnit}
        tooltip={planIsMultiUnit ? ARCHIVE_MULTI_UNIT_PLAN_MESSAGE : undefined}
      >
        <IconFolder size={14} />
        Archive Plan
      </Link>,
    );
  }

  if (
    onUnarchivePlan &&
    ((publicPlanType === "master_plans" && hasPermission("EDIT_MASTER_PLANS")) || canEditCommunityPlan)
  ) {
    menuItems.push(
      <Link key="unarchive_plan" onClick={onUnarchivePlan}>
        <IconFolder size={14} />
        Unarchive Plan
      </Link>,
    );
  }

  if (onDeletePlan && hasPermission("DELETE_HOME_PLANS")) {
    menuItems.push(
      <Link key="delete_plan" onClick={onDeletePlan}>
        <IconTrash size={14} />
        Delete Plan
      </Link>,
    );
  }

  return menuItems;
}
